import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { BehaviorSubject, Observable } from 'rxjs';

import { RequisitionsListParam } from '@recruitee/requisitions-types';
import type { RequisitionsGroupKind, RequisitionsParams } from '@recruitee/requisitions-types';
import { SortDirection } from '@recruitee/search-types';

import {
  RequisitionsListViewType,
  RequisitionsTableColumn,
  RequisitionsViewState,
} from '../requisitions.types';
import { RequisitionsListCacheService } from './requisitions-list-cache.service';

@Injectable()
export class RequisitionsListViewStateService {
  public viewState$: Observable<RequisitionsViewState>;
  private _viewState$ = new BehaviorSubject<RequisitionsViewState>(
    this.cacheService.getLocalStorageData(),
  );

  constructor(
    private cacheService: RequisitionsListCacheService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.viewState$ = this._viewState$.asObservable();
  }

  private get viewState(): RequisitionsViewState {
    return this._viewState$.getValue();
  }

  public initializeQueryParamsFromCache(): void {
    const { groupBy } = this.cacheService.getLocalStorageData();
    this.changeParams({ [RequisitionsListParam.GroupBy]: groupBy });
  }

  public setViewType(viewType: RequisitionsListViewType): void {
    this._viewState$.next({ ...this.viewState, viewType });
    this.cacheService.saveDataInLocalStorage({ viewType });
  }

  public setGroupVisibility(groupName: string): void {
    const currentCollapsedGroups = this.viewState.collapsedGroups;
    const collapsedGroups = currentCollapsedGroups.includes(groupName)
      ? currentCollapsedGroups.filter(group => group !== groupName)
      : [...currentCollapsedGroups, groupName];

    this._viewState$.next({ ...this.viewState, collapsedGroups });
    this.cacheService.saveDataInLocalStorage({ collapsedGroups });
  }

  public setGroup(groupBy: RequisitionsGroupKind | null): void {
    this.changeParams({ [RequisitionsListParam.GroupBy]: groupBy });
    this.cacheService.saveDataInLocalStorage({ groupBy });
  }

  public setSorting({ id, sort }: RequisitionsTableColumn): void {
    const nextSort: SortDirection =
      sort === SortDirection.Desc ? SortDirection.Asc : SortDirection.Desc;

    this.changeParams({
      [RequisitionsListParam.SortBy]: id,
      [RequisitionsListParam.SortOrder]: nextSort,
    });
  }

  public changeParams(queryParams: Partial<RequisitionsParams>) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
